import FetchUtils, { parseJSON } from './fetch_utils';
import { logoutSuccess } from '../actions/session_actions';
import {
	fetchAllMessagesError,
	fetchAllMessagesStart,
	fetchAllMessagesSuccess,
} from '../actions/messages_actions';
import { WORK_ORDERS_CRUD_ACTION_CREATORS } from '../actions/work_orders_actions';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { ROLE_TYPES } from '../utils/DataConstants';
import { getInternalNotesChannelId } from '../utils/WorkOrderUtils';
import {
	NOTE_TYPE_LOG,
	NOTE_TYPE_NOTE,
	NOTE_TYPE_INTERNAL_ATTACHMENT_LOG,
	NOTE_TYPE_SERVICE_CALL_CHECK_IN,
	NOTE_TYPE_SERVICE_CALL_CHECK_OUT,
} from '../constants/NoteTypes';

const fetchUtils = new FetchUtils();
const baseUrl = getBackendUri();

type InternalWorkspaceGetOptions = {
	clearChannelBeforeFetch?: boolean;
};

export const mergeAndSortData = (
	data: {
		notes?: any[];
		logs?: any[];
		serviceCallCheckInCheckOutDetails?: any[];
		internalAttachmentLogs?: any[];
	} = {
		notes: [],
		logs: [],
		serviceCallCheckInCheckOutDetails: [],
		internalAttachmentLogs: [],
	}
) => {
	const notes = (data.notes || []).map((note) => ({
		...note,
		type: NOTE_TYPE_NOTE,
		timestamp: new Date(note.noteAddedAt).getTime(),
	}));

	const logs = (data.logs || []).map((log) => ({
		...log,
		type: NOTE_TYPE_LOG,
		timestamp: new Date(log.changedAt).getTime(),
	}));

	const internalAttachmentLogs = (data.internalAttachmentLogs || []).map((log) => ({
		...log,
		type: NOTE_TYPE_INTERNAL_ATTACHMENT_LOG,
		timestamp: new Date(log.changedAt).getTime(),
	}));

	const serviceCallDetails = data.serviceCallCheckInCheckOutDetails || [];

	const serviceCallCheckIns = serviceCallDetails
		.filter(function (sc) {
			return sc.checkInTime;
		})
		.map(function (sc) {
			return {
				...sc,
				type: NOTE_TYPE_SERVICE_CALL_CHECK_IN,
				timestamp: new Date(sc.checkInTime).getTime(),
			};
		});

	const serviceCallCheckOuts = serviceCallDetails
		.filter(function (sc) {
			return sc.checkOutTime;
		})
		.map(function (sc) {
			return {
				...sc,
				type: NOTE_TYPE_SERVICE_CALL_CHECK_OUT,
				timestamp: new Date(sc.checkOutTime).getTime(),
			};
		});

	const mergedData = [
		...notes,
		...logs,
		...internalAttachmentLogs,
		...serviceCallCheckIns,
		...serviceCallCheckOuts,
	].sort((a, b) => a.timestamp - b.timestamp);

	return mergedData.map((entry) => {
		const { timestamp, ...rest } = entry;
		return rest;
	});
};

/**
 * Normalizes a notes API response into a flat array of notes that the common
 * notes module can consume. Handles both shapes:
 *  - Structured (WO): { notes, logs, serviceCallCheckInCheckOutDetails }
 *    → delegated to mergeAndSortData
 *  - Flat array (Walkthrough, Bidder): [{ note }, { note }]
 *    → tagged with type: NOTE_TYPE_NOTE and sorted by noteAddedAt
 */
export const normalizeNotesResponse = (data) => {
	if (Array.isArray(data)) {
		return data
			.map((note) => ({
				...note,
				type: note.type || NOTE_TYPE_NOTE,
				timestamp: new Date(note.noteAddedAt).getTime(),
			}))
			.sort((a, b) => a.timestamp - b.timestamp)
			.map((entry) => {
				const { timestamp, ...rest } = entry;
				return rest;
			});
	}
	return mergeAndSortData(data);
};

const fetchAllWorkOrderNotes = (roleType) => (workOrderId) => {
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(fetchAllMessagesStart(workOrderId));
			fetchUtils
				.get(baseUrl + `/api/v1/${roleType}/work_order/work_order_notes_with_logs/${workOrderId}`, {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				})
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(fetchAllMessagesError(d.message, workOrderId));
						reject(d.message);
					} else {
						dispatch(fetchAllMessagesSuccess(workOrderId, mergeAndSortData(d.data)));
						resolve(mergeAndSortData(d.data));
					}
				})
				.catch((d) => {
					dispatch(fetchAllMessagesError(d.message, workOrderId));
					reject(d.message);
				});
		});
	};
};

export const fetchAllWorkOrderNotesForBuyer = fetchAllWorkOrderNotes(ROLE_TYPES.BUYER);
export const fetchAllWorkOrderNotesForSupplier = fetchAllWorkOrderNotes(ROLE_TYPES.SUPPLIER);

export const fetchAllContractorWorkOrderNotes = (roleType) => (workOrderId) => {
	return (dispatch) => {
		return new Promise((resolve, reject) => {
			dispatch(fetchAllMessagesStart(workOrderId));
			fetchUtils
				.get(
					baseUrl + `/api/v1/${roleType}/work_order/contractor_work_order_notes/${workOrderId}`,
					{
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
					}
				)
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(fetchAllMessagesError(d.message, workOrderId));
						reject(d.message);
					} else {
						const normalized = normalizeNotesResponse(d.data);
						dispatch(fetchAllMessagesSuccess(workOrderId, normalized));
						resolve(normalized);
					}
				})
				.catch((d) => {
					dispatch(fetchAllMessagesError(d.message, workOrderId));
					reject(d.message);
				});
		});
	};
};

export const applyInternalWorkspacePayloadToStore = (workOrderId, responseData) => {
	return (dispatch) => {
		const channelId = getInternalNotesChannelId(workOrderId);
		const sortedNotes = mergeAndSortData(
			Array.isArray(responseData) ? { notes: responseData } : responseData || {}
		);
		const extras: any = {};
		if (responseData && Array.isArray(responseData.internalAttachments)) {
			extras.internalAttachments = responseData.internalAttachments;
		}
		if (responseData && Array.isArray(responseData.subscribers)) {
			extras.subscribers = responseData.subscribers;
		}
		dispatch(fetchAllMessagesSuccess(channelId, sortedNotes, extras));
		if (responseData && Array.isArray(responseData.internalAttachments)) {
			dispatch(
				WORK_ORDERS_CRUD_ACTION_CREATORS.updatePartialSuccess({
					id: workOrderId,
					supplierInternalAttachments: responseData.internalAttachments,
				})
			);
		}
		return {
			notes: sortedNotes,
			subscribers: (responseData && responseData.subscribers) || [],
		};
	};
};

const fetchAllInternalNotes =
	(roleType) =>
	(workOrderId, options: InternalWorkspaceGetOptions = {}) => {
		return (dispatch) => {
			const channelId = getInternalNotesChannelId(workOrderId);
			const clearChannelBeforeFetch = options.clearChannelBeforeFetch === true;
			return new Promise((resolve, reject) => {
				if (clearChannelBeforeFetch) {
					dispatch(fetchAllMessagesStart(channelId));
				}
				fetchUtils
					.get(
						baseUrl + `/api/v1/${roleType}/work_order/work_order_internal_space/${workOrderId}`,
						{
							headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						}
					)
					.then(parseJSON)
					.then(({ json, status }) => {
						const d = json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (d.status === 'error') {
							dispatch(fetchAllMessagesError(d.message, channelId));
							reject(d.message);
						} else {
							const merged = dispatch(applyInternalWorkspacePayloadToStore(workOrderId, d.data));
							resolve(merged.notes);
						}
					})
					.catch((d) => {
						dispatch(fetchAllMessagesError(d.message, channelId));
						reject(d.message);
					});
			});
		};
	};

export const fetchAllInternalNotesForBuyer = fetchAllInternalNotes(ROLE_TYPES.BUYER);
export const fetchAllInternalNotesForSupplier = fetchAllInternalNotes(ROLE_TYPES.SUPPLIER);

/**
 * @param options.clearChannelBeforeFetch When true (first hydrate only), runs `FETCH_ALL_MESSAGES_START`
 * before the GET. Omit or false so refreshes (websocket / subscribers) do not clear the timeline.
 */
export const fetchInternalWorkspaceForSupplier =
	(workOrderId, options: InternalWorkspaceGetOptions = {}) =>
	(dispatch) => {
		const channelId = getInternalNotesChannelId(workOrderId);
		const clearChannelBeforeFetch = options.clearChannelBeforeFetch === true;
		return new Promise((resolve, reject) => {
			if (clearChannelBeforeFetch) {
				dispatch(fetchAllMessagesStart(channelId));
			}
			fetchUtils
				.get(baseUrl + `/api/v1/supplier/work_order/work_order_internal_space/${workOrderId}`, {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				})
				.then(parseJSON)
				.then(({ json, status }) => {
					const d = json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (d.status === 'error') {
						dispatch(fetchAllMessagesError(d.message, channelId));
						reject(d.message);
					} else {
						const merged = dispatch(applyInternalWorkspacePayloadToStore(workOrderId, d.data));
						resolve(merged);
					}
				})
				.catch((e) => {
					dispatch(fetchAllMessagesError(e.message, channelId));
					reject(e.message);
				});
		});
	};
